/* Reusable blanket fields. All prices and surcharges are native Shopify variant prices. */
(function (root, factory) {
  'use strict';
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root) {
    root.StapleBlanketFields = { attach: api.attach };
    api.bootstrap(root);
  }
})(typeof window === 'undefined' ? null : window, function () {
  'use strict';

  // Liquid truncates product.variants at 250. Without a verified total count,
  // exactly 250 cannot prove that this is the complete catalog.
  const MAX_VARIANTS = 249;
  const MAX_PHOTO_BYTES = 10 * 1024 * 1024;
  const CONTROL_CHARACTERS = /\p{Cc}/u;
  const PROFILES = ['optional_name', 'variant_name', 'photo_proof'];
  const MESSAGES = Object.freeze({
    loading: 'Checking your blanket options…',
    config: 'These personalization options could not be verified. Please reload this page before adding the blanket.',
    choice: 'Please choose an available blanket option and wait for its selection to finish updating.',
    selectedNoName: 'No name will be added.',
    selectedCustom: 'Enter the name for your selected blanket.',
    selectedPhoto: 'Add the name and photo for your blanket.',
    count: '{count} / {max} characters',
    required: 'Please enter the recipient’s name.',
    controls: 'Please enter a name without control characters.',
    tooLong: 'Please use {max} characters or fewer for the name.',
    year: 'Leave the year blank or enter four digits.',
    photoSelected: 'Photo selected. It will be attached when you add this blanket to your cart.',
    photoUnselected: 'No photo selected.',
    photoRequired: 'Please choose a photo for your blanket.',
    photoType: 'Please choose one nonempty JPG, PNG or WebP image.',
    photoSize: 'Please choose an image of 10 MiB or smaller.'
  });

  function dictionary(value) {
    return value !== null && typeof value === 'object' && !Array.isArray(value) &&
      (Object.getPrototypeOf(value) === Object.prototype || Object.getPrototypeOf(value) === null);
  }
  function nonempty(value) {
    return typeof value === 'string' && value.trim().length > 0 && !CONTROL_CHARACTERS.test(value);
  }
  function idString(value) {
    if (typeof value === 'number' && (!Number.isSafeInteger(value) || value <= 0)) return null;
    return (typeof value === 'number' || typeof value === 'string') && /^[1-9][0-9]*$/.test(String(value)) ? String(value) : null;
  }
  function validProperty(value) {
    return nonempty(value) && value === value.trim() && !/[\[\]]/u.test(value);
  }
  function invalidConfig(reason) { return { ok: false, key: 'config', reason }; }

  /* Pure model. Invalid product metadata never falls back to a free-name flow. */
  function validateConfig(raw) {
    if (!dictionary(raw) || !PROFILES.includes(raw.profile)) return invalidConfig('profile');
    if (!nonempty(raw.formId) || !nonempty(raw.pickerId) || /\s/u.test(raw.formId + raw.pickerId)) return invalidConfig('element_ids');
    if (!Array.isArray(raw.optionNames) || raw.optionNames.length < 1 || raw.optionNames.length > 3 ||
        !raw.optionNames.every(nonempty) || new Set(raw.optionNames).size !== raw.optionNames.length) return invalidConfig('option_names');
    if (!nonempty(raw.personalizationOption) || !nonempty(raw.noNameValue) || !nonempty(raw.customNameValue) ||
        raw.noNameValue === raw.customNameValue) return invalidConfig('personalization_values');
    if (!Number.isSafeInteger(raw.maxNameLength) || raw.maxNameLength <= 0 || raw.maxNameLength > 50 || !validProperty(raw.nameProperty)) return invalidConfig('name_settings');
    // The unchanged product-form.js AJAX path explicitly writes this property.
    if (raw.profile !== 'photo_proof' && raw.nameProperty !== 'Recipient name') return invalidConfig('native_name_property');
    if (typeof raw.yearEnabled !== 'boolean' || typeof raw.requiredPhoto !== 'boolean' ||
        (raw.profile !== 'photo_proof' && (raw.yearEnabled || raw.requiredPhoto)) ||
        (raw.profile === 'photo_proof' && !raw.requiredPhoto)) return invalidConfig('photo_settings');
    if (raw.previewMode !== undefined && typeof raw.previewMode !== 'boolean') return invalidConfig('preview_mode');
    if (!dictionary(raw.messages) || !Object.values(raw.messages).every(value => typeof value === 'string')) return invalidConfig('messages');
    if (!Array.isArray(raw.variants) || !raw.variants.length || raw.variants.length > MAX_VARIANTS) return invalidConfig('variant_count');

    const personalizationIndex = raw.optionNames.indexOf(raw.personalizationOption);
    if ((raw.profile === 'variant_name') !== (personalizationIndex !== -1)) return invalidConfig('personalization_option');
    const ids = new Set(), combinations = new Set(), pairs = new Map(), variants = [];
    for (const variant of raw.variants) {
      if (!dictionary(variant)) return invalidConfig('variant');
      const id = idString(variant.id);
      if (!id || ids.has(id) || !Array.isArray(variant.options) || variant.options.length !== raw.optionNames.length ||
          !variant.options.every(nonempty) || typeof variant.available !== 'boolean' ||
          !Number.isSafeInteger(variant.price) || variant.price < 0) return invalidConfig('variant');
      const key = JSON.stringify(variant.options);
      if (combinations.has(key)) return invalidConfig('duplicate_combination');
      ids.add(id); combinations.add(key);
      const copy = Object.freeze({ id, options: Object.freeze(variant.options.slice()), available: variant.available, price: variant.price });
      variants.push(copy);
      if (personalizationIndex !== -1) {
        const choice = variant.options[personalizationIndex];
        if (choice !== raw.noNameValue && choice !== raw.customNameValue) return invalidConfig('unknown_personalization');
        const pairKey = JSON.stringify(variant.options.filter((_, index) => index !== personalizationIndex));
        const pair = pairs.get(pairKey) || new Map();
        pair.set(choice, copy); pairs.set(pairKey, pair);
      }
    }
    for (const pair of pairs.values()) {
      const base = pair.get(raw.noNameValue), custom = pair.get(raw.customNameValue);
      if (!base || !custom || custom.price <= base.price) return invalidConfig('incomplete_or_unpriced_pair');
    }
    const config = Object.freeze({
      formId: raw.formId, pickerId: raw.pickerId, profile: raw.profile,
      optionNames: Object.freeze(raw.optionNames.slice()), variants: Object.freeze(variants),
      personalizationOption: raw.personalizationOption, noNameValue: raw.noNameValue, customNameValue: raw.customNameValue,
      maxNameLength: raw.maxNameLength, nameProperty: raw.nameProperty,
      yearEnabled: raw.yearEnabled, requiredPhoto: raw.requiredPhoto, previewMode: raw.previewMode === true,
      messages: Object.freeze(Object.assign(Object.create(null), raw.messages))
    });
    return Object.freeze({ ok: true, config, personalizationIndex });
  }

  function normalizeName(raw) { return String(raw).normalize('NFC').replace(/\s+/gu, ' ').trim(); }
  function validateName(raw, maxLength) {
    if (typeof raw !== 'string' || CONTROL_CHARACTERS.test(raw)) return { ok: false, key: 'controls' };
    const value = normalizeName(raw);
    if (!value) return { ok: false, key: 'required' };
    if (Array.from(value).length > maxLength) return { ok: false, key: 'tooLong' };
    return { ok: true, value };
  }
  function validateYear(raw) {
    if (typeof raw !== 'string' || CONTROL_CHARACTERS.test(raw)) return { ok: false, key: 'year' };
    const value = raw.trim();
    return !value || /^[0-9]{4}$/.test(value) ? { ok: true, value } : { ok: false, key: 'year' };
  }
  function validatePhoto(file, required) {
    if (!file) return required ? { ok: false, key: 'photoRequired' } : { ok: true };
    const mime = typeof file.type === 'string' ? file.type.toLowerCase() : '';
    const allowed = ['image/jpeg', 'image/png', 'image/webp'];
    const validType = allowed.includes(mime) || (!mime && typeof file.name === 'string' && /\.(jpe?g|png|webp)$/i.test(file.name));
    if (!validType || !Number.isSafeInteger(file.size) || file.size <= 0) return { ok: false, key: 'photoType' };
    return file.size > MAX_PHOTO_BYTES ? { ok: false, key: 'photoSize' } : { ok: true };
  }
  function selectedVariant(model, options) {
    if (!model || !model.ok || !Array.isArray(options) || options.length !== model.config.optionNames.length || !options.every(nonempty)) return null;
    return model.config.variants.find(variant => variant.options.every((value, index) => value === options[index])) || null;
  }
  function validateSelection(model, options, nativeId) {
    const variant = selectedVariant(model, options);
    return variant && variant.available && idString(nativeId) === variant.id ? { ok: true, variant } : { ok: false, key: 'choice' };
  }

  class BlanketController {
    constructor(host) {
      this.host = host;
      this.document = host.ownerDocument;
      this.window = this.document.defaultView;
      this.disconnected = false;
      this.invalidTargets = new Set();
      this.input = host.querySelector('[data-name-input]');
      host.nameInput = this.input;
      this.panel = host.querySelector('[data-name-panel]');
      this.counter = host.querySelector('[data-name-count]');
      this.error = host.querySelector('[data-personalization-error]');
      this.status = host.querySelector('[data-blanket-status]');
      this.namePreview = host.querySelector('[data-name-preview]');
      this.yearPreview = host.querySelector('[data-year-preview]');
      this.photoStatus = host.querySelector('[data-photo-status]');
      this.year = host.querySelector('[data-year-input]');
      this.photo = host.querySelector('[data-photo-input]');
      this.choices = Array.from(host.querySelectorAll('[data-name-choice]'));
      this.form = host.closest('form');
      try { this.model = validateConfig(JSON.parse(host.querySelector('[data-blanket-config]').textContent)); }
      catch (_) { this.model = invalidConfig('json'); }
      this.config = this.model.ok ? this.model.config : null;
      this.picker = this.config ? this.document.getElementById(this.config.pickerId) : null;
      this.button = this.form && this.form.querySelector('[type="submit"]');
      this.validMarkup = this.checkMarkup();
      if (this.validMarkup && this.config.profile === 'optional_name') {
        this.choices.forEach(choice => { choice.checked = choice.value === 'none'; });
      }
      this.onChange = event => {
        const target = event.target;
        if (this.host.contains(target) || (this.picker && this.picker.contains(target)) || (target && target.form === this.form)) {
          this.clearError(); this.sync();
        }
      };
      this.onInput = () => { this.clearError(); this.updateCount(); this.updatePreview(); };
      this.onPageShow = () => {
        // Back/forward cache restores the form after a native multipart submission.
        const productForm = this.host.closest('product-form');
        if (productForm) delete productForm.dataset.stapleSubmitting;
        this.sync();
      };
      this.onSectionLoad = () => this.sync();
      this.document.addEventListener('change', this.onChange);
      this.host.addEventListener('input', this.onInput);
      this.window.addEventListener('pageshow', this.onPageShow);
      this.document.addEventListener('shopify:section:load', this.onSectionLoad);
      const Observer = this.window.MutationObserver;
      if (typeof Observer === 'function') {
        this.observer = new Observer(records => {
          if (records.some(record => !this.host.contains(record.target))) this.sync();
        });
        this.observe();
      }
      this.sync();
    }

    belongsToForm(input) { return input && input.form === this.form; }
    propertyInput(input) { return this.belongsToForm(input) && /^properties\[[^\[\]\p{Cc}]+\]$/u.test(input.name || ''); }
    defaultOnly() {
      return Boolean(this.config && this.config.profile !== 'variant_name' && this.config.variants.length === 1 &&
        this.config.optionNames.length === 1 && this.config.optionNames[0] === 'Title' && this.config.variants[0].options[0] === 'Default Title');
    }
    initialLock() {
      if (!this.form) return { valid: false, element: null };
      const locks = Array.from(this.form.querySelectorAll('[data-blanket-initial-lock]')).filter(element => element.hasAttribute('data-blanket-initial-lock'));
      if (locks.length > 1) return { valid: false, element: null };
      const element = locks[0] || null;
      // A fieldset keeps the button's own disabled state authoritative for native
      // section-rendering code that reads availability from returned HTML.
      const valid = !element || element === this.button ||
        (element.tagName === 'FIELDSET' && this.belongsToForm(element) && element.contains(this.button));
      return { valid, element };
    }
    restorePreview() {
      const state = this.previewState;
      if (!state) return;
      for (const key of ['staplePreviewQuery', 'updateUrl']) {
        if (state.picker.dataset[key] !== state.applied[key]) continue;
        if (state.previous[key] === undefined) delete state.picker.dataset[key];
        else state.picker.dataset[key] = state.previous[key];
      }
      this.previewState = null;
    }
    configurePreview() {
      if (!this.config?.previewMode || !this.picker) return true;
      if (typeof this.window.URLSearchParams !== 'function' || typeof this.window.location?.search !== 'string') return false;
      if (this.previewState && this.previewState.picker !== this.picker) this.restorePreview();
      if (!this.previewState) {
        this.previewState = { picker: this.picker, previous: {
          staplePreviewQuery: this.picker.dataset.staplePreviewQuery, updateUrl: this.picker.dataset.updateUrl
        }, applied: {} };
      }
      const current = new this.window.URLSearchParams(this.window.location.search);
      const allowed = new this.window.URLSearchParams();
      for (const key of ['view', 'preview_theme_id']) {
        const value = current.get(key);
        if (value) allowed.set(key, value);
      }
      const query = allowed.toString();
      const applied = { staplePreviewQuery: query ? '&' + query : '', updateUrl: 'false' };
      for (const key of Object.keys(applied)) this.picker.dataset[key] = applied[key];
      this.previewState.applied = applied;
      return true;
    }
    checkMarkup() {
      const c = this.config;
      if (!c || this.host.dataset.reusable !== 'true' || this.host.dataset.mode !== (c.profile === 'photo_proof' ? 'photo' : 'name') ||
          !this.form || this.form.getAttribute('id') !== c.formId || this.document.getElementById(c.formId) !== this.form || (!this.picker && !this.defaultOnly()) ||
          !this.input || this.input.type !== 'text' || !this.belongsToForm(this.input) || this.input.name !== 'properties[' + c.nameProperty + ']' ||
          !this.panel || !this.counter || !this.error || !this.status || !this.button || !this.belongsToForm(this.button) || !this.initialLock().valid) return false;
      if (c.profile === 'optional_name') {
        if (this.choices.length !== 2 || !this.choices.every(choice => this.belongsToForm(choice) && choice.type === 'radio' && !choice.disabled) ||
            this.choices[0].name !== this.choices[1].name || !nonempty(this.choices[0].name) ||
            this.choices.some(choice => choice.name.startsWith('properties[')) ||
            !['none', 'name'].every(value => this.choices.some(choice => choice.value === value))) return false;
      } else if (this.choices.length) return false;
      if (c.profile === 'photo_proof') {
        if (!this.photo || this.photo.type !== 'file' || this.photo.multiple || !this.propertyInput(this.photo) ||
            (c.yearEnabled && (!this.year || this.year.type !== 'text' || !this.propertyInput(this.year)))) return false;
        if ((this.form.getAttribute('enctype') || '').toLowerCase() !== 'multipart/form-data' ||
            (this.form.getAttribute('method') || '').toLowerCase() !== 'post') return false;
      } else if (this.photo || this.year) return false;
      return true;
    }
    observe() {
      if (!this.observer) return;
      this.observer.disconnect();
      if (this.form) this.observer.observe(this.form, { subtree: true, childList: true, attributes: true, attributeFilter: ['value', 'disabled', 'aria-disabled', 'aria-busy', 'data-unavailable', 'class'] });
      if (this.picker && !this.form?.contains(this.picker)) this.observer.observe(this.picker, { subtree: true, childList: true, attributes: true, attributeFilter: ['checked', 'selected', 'disabled', 'value'] });
    }
    readOptions() {
      if (!this.validMarkup) return null;
      const groups = Array.from(this.picker?.querySelectorAll('.product-form__input') || []);
      // A native single-default-variant product may have no rendered picker controls.
      if (!groups.length && this.defaultOnly()) {
        return ['Default Title'];
      }
      if (groups.length !== this.config.optionNames.length) return null;
      const options = [];
      for (const group of groups) {
        const radios = Array.from(group.querySelectorAll('input[type="radio"]'));
        const selects = Array.from(group.querySelectorAll('select'));
        if (radios.length && !selects.length) {
          const checked = radios.filter(input => input.checked);
          if (checked.length !== 1 || checked[0].disabled || !radios.every(input => this.belongsToForm(input)) ||
              new Set(radios.map(input => input.name)).size !== 1 || !nonempty(radios[0].name)) return null;
          options.push(checked[0].value);
        } else if (!radios.length && selects.length === 1) {
          const select = selects[0];
          if (!this.belongsToForm(select) || select.disabled || select.multiple || select.selectedOptions?.length !== 1 || select.selectedOptions[0].disabled) return null;
          options.push(select.value);
        } else return null;
      }
      return options;
    }
    nativeId() {
      if (!this.form) return null;
      const fields = Array.from(this.form.querySelectorAll('[name="id"]'));
      return fields.length === 1 && this.belongsToForm(fields[0]) && !fields[0].disabled ? fields[0].value : null;
    }
    choice() {
      if (!this.config) return null;
      if (this.config.profile !== 'optional_name') return null;
      const checked = this.choices.filter(input => input.checked && !input.disabled);
      return checked.length === 1 && ['none', 'name'].includes(checked[0].value) ? checked[0].value : null;
    }
    message(key) {
      const value = this.config?.messages[key] || MESSAGES[key] || MESSAGES.config;
      return value.replace(/\{max\}/g, String(this.config?.maxNameLength || '')).replace(/\{count\}/g, String(Array.from(normalizeName(this.input?.value || '')).length));
    }
    updateCount() { if (this.counter) this.counter.textContent = this.message('count'); }
    updatePreview() {
      if (this.namePreview) this.namePreview.textContent = this.input && !this.input.disabled ? normalizeName(this.input.value) : this.message('selectedNoName');
      if (this.yearPreview) this.yearPreview.textContent = this.year && !this.year.disabled ? this.year.value.trim() : '';
      if (this.photoStatus) {
        const file = this.photo && !this.photo.disabled ? this.photo.files?.[0] : null;
        const checked = validatePhoto(file, false);
        const key = (this.photo?.files?.length || 0) > 1 ? 'photoType' : !checked.ok ? checked.key : file ? 'photoSelected' : 'photoUnselected';
        this.photoStatus.textContent = this.message(key);
      }
    }
    clearError() {
      if (this.error) { this.error.hidden = true; this.error.textContent = ''; }
      for (const target of this.invalidTargets) target.removeAttribute('aria-invalid');
      this.invalidTargets.clear();
    }
    nativeLocked() {
      const productForm = this.host.closest('product-form');
      return this.button.getAttribute('aria-disabled') === 'true' || this.button.getAttribute('aria-busy') === 'true' ||
        this.button.dataset.unavailable === 'true' || this.button.classList.contains('loading') ||
        productForm?.dataset.stapleSubmitting === 'true';
    }
    sync() {
      if (this.disconnected) return;
      if (this.config) {
        const picker = this.document.getElementById(this.config.pickerId);
        if (picker !== this.picker) { this.restorePreview(); this.picker = picker; this.observe(); }
      }
      this.button = this.form && this.form.querySelector('[type="submit"]');
      this.validMarkup = this.checkMarkup() && this.configurePreview();
      const options = this.readOptions();
      const variant = this.validMarkup ? selectedVariant(this.model, options) : null;
      const optionalChoice = this.choice();
      const active = Boolean(this.validMarkup && (this.config.profile === 'photo_proof' ||
        (this.config.profile === 'variant_name' && variant?.options[this.model.personalizationIndex] === this.config.customNameValue) ||
        (this.config.profile === 'optional_name' && optionalChoice === 'name')));
      if (this.panel) this.panel.hidden = !active;
      if (this.input) {
        this.input.disabled = !active; this.input.required = active;
        if (!active) this.input.value = '';
      }
      if (this.year) {
        this.year.disabled = !this.validMarkup || !this.config.yearEnabled;
        this.year.required = false;
        if (this.year.disabled) this.year.value = '';
      }
      if (this.photo) {
        this.photo.disabled = !this.validMarkup;
        this.photo.required = Boolean(this.validMarkup && this.config.requiredPhoto);
      }
      this.updateCount(); this.updatePreview();
      const selection = validateSelection(this.model, options, this.nativeId());
      const ready = this.validMarkup && selection.ok && (this.config.profile !== 'optional_name' || optionalChoice !== null);
      if (this.status) this.status.textContent = !this.validMarkup ? this.message('config') : !ready ? this.message('loading') :
        this.message(this.config.profile === 'photo_proof' ? 'selectedPhoto' : active ? 'selectedCustom' : 'selectedNoName');
      // Never remove a native request/sold-out lock or introduce a second price mechanism.
      // Only our server-rendered lock is ours to release. Prefer a fieldset so
      // returned section HTML does not make an available button look sold out.
      const lock = this.initialLock();
      if (lock.valid && lock.element) {
        if (ready && !this.nativeLocked()) {
          if (lock.element.disabled) lock.element.disabled = false;
          lock.element.removeAttribute('data-blanket-initial-lock');
        } else if (!lock.element.disabled) lock.element.disabled = true;
      }
    }
    fail(key, input) {
      if (this.error) { this.error.textContent = this.message(key); this.error.hidden = false; }
      const target = input && !input.disabled ? input : this.picker?.querySelector('input[type="radio"]:checked, select');
      if (target) {
        target.setAttribute('aria-invalid', 'true'); this.invalidTargets.add(target);
        target.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
        target.focus?.({ preventScroll: true });
      }
      return false;
    }
    validate() {
      if (this.disconnected) return false;
      this.clearError(); this.sync();
      if (!this.validMarkup) return this.fail('config');
      const selection = validateSelection(this.model, this.readOptions(), this.nativeId());
      if (!selection.ok) return this.fail('choice');
      if (this.config.profile === 'optional_name' && this.choice() === null) return this.fail('choice');
      if (!this.input.disabled) {
        const name = validateName(this.input.value, this.config.maxNameLength);
        if (!name.ok) return this.fail(name.key, this.input);
        this.input.value = name.value; this.updateCount(); this.updatePreview();
      }
      if (this.config.profile === 'photo_proof') {
        if (this.config.yearEnabled) {
          const year = validateYear(this.year.value);
          if (!year.ok) return this.fail(year.key, this.year);
          this.year.value = year.value; this.updatePreview();
        }
        if ((this.photo.files?.length || 0) > 1) return this.fail('photoType', this.photo);
        const photo = validatePhoto(this.photo.files?.[0], this.config.requiredPhoto);
        if (!photo.ok) return this.fail(photo.key, this.photo);
      }
      return true;
    }
    disconnect() {
      if (this.disconnected) return;
      this.disconnected = true;
      this.document.removeEventListener('change', this.onChange);
      this.host.removeEventListener('input', this.onInput);
      this.window.removeEventListener('pageshow', this.onPageShow);
      this.document.removeEventListener('shopify:section:load', this.onSectionLoad);
      this.observer?.disconnect();
      this.restorePreview();
      this.clearError();
      if (this.host.blanketController === this) delete this.host.blanketController;
    }
  }
  function attach(host) {
    if (!host || typeof host.querySelector !== 'function') throw new TypeError('A blanket personalization element is required.');
    if (host.blanketController && !host.blanketController.disconnected) return host.blanketController;
    const controller = new BlanketController(host);
    host.blanketController = controller;
    return controller;
  }

  function bootstrap(root) {
    const document = root?.document;
    if (!document || typeof document.querySelectorAll !== 'function') return;
    const key = Symbol.for('staple.blanket.fields.bootstrap');
    if (root[key]) {
      if (document.readyState !== 'loading') root[key].scan(document);
      return;
    }
    const selector = 'staple-personalization[data-reusable="true"]';
    const scan = scope => {
      if (!scope || typeof scope.querySelectorAll !== 'function') return;
      const hosts = scope.matches?.(selector) ? [scope] : [];
      hosts.push(...scope.querySelectorAll(selector));
      for (const host of hosts) {
        try { root.StapleBlanketFields.attach(host); }
        catch (_) {
          const status = host.querySelector('[data-blanket-status]');
          if (status) status.textContent = MESSAGES.config;
          // Keep the server-rendered disabled lock in place on any load failure.
        }
      }
    };
    const onSectionLoad = event => scan(event.target || document);
    const onReady = () => { document.removeEventListener('DOMContentLoaded', onReady); scan(document); };
    Object.defineProperty(root, key, { value: { scan } });
    document.addEventListener('shopify:section:load', onSectionLoad);
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', onReady);
    else scan(document);
  }

  return { attach, bootstrap, validateConfig, normalizeName, validateName, validateYear, validatePhoto, selectedVariant, validateSelection, MAX_PHOTO_BYTES, MAX_VARIANTS };
});
